import { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink, Handshake } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SponsorsPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API}/sponsors`);
      setSponsors(response.data);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'principal': return 'Patrocinador Principal';
      case 'secundario': return 'Patrocinador';
      default: return 'Apoio';
    }
  };

  const principalSponsors = sponsors.filter(s => s.tier === 'principal');
  const secondarySponsors = sponsors.filter(s => s.tier === 'secundario');
  const supportSponsors = sponsors.filter(s => s.tier === 'apoio');

  return (
    <div className="min-h-screen pt-20" data-testid="sponsors-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Parceiros</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">PATROCINADORES</h1>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 section-padding bg-[#0A0A0A] border-b border-white/10">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <p className="text-gray-400 leading-relaxed">
            O GDR Esporões agradece a todos os patrocinadores e parceiros que apoiam o clube. 
            Juntos, construímos o futuro do futebol em Esporões e contribuímos para o crescimento 
            da nossa comunidade.
          </p>
        </div>
      </section>

      {/* Main Sponsors */}
      {principalSponsors.length > 0 && (
        <section className="py-24 section-padding" data-testid="principal-sponsors">
          <div className="container-custom">
            <div className="text-center mb-16">
              <span className="inline-block bg-white text-black px-4 py-2 text-xs uppercase tracking-widest font-bold mb-4">
                Patrocinadores Principais
              </span>
              <h2 className="font-heading text-4xl font-black">OS NOSSOS PARCEIROS DE EXCELÊNCIA</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {principalSponsors.map((sponsor) => (
                <a
                  key={sponsor.id}
                  href={sponsor.website || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-white/10 p-8 hover:border-white/30 transition-all bg-[#0A0A0A]"
                  data-testid={`sponsor-principal-${sponsor.id}`}
                >
                  <div className="aspect-video flex items-center justify-center mb-6">
                    <img
                      src={sponsor.logo_url}
                      alt={sponsor.name}
                      className="max-h-24 w-auto object-contain grayscale group-hover:grayscale-0 transition-all"
                    />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-center">{sponsor.name}</h3>
                  {sponsor.description && (
                    <p className="text-gray-400 text-sm text-center mt-2">{sponsor.description}</p>
                  )}
                  {sponsor.website && (
                    <div className="flex items-center justify-center gap-2 mt-4 text-gray-500 group-hover:text-white transition-colors">
                      <ExternalLink size={14} />
                      <span className="text-xs uppercase tracking-widest">Visitar</span>
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Secondary Sponsors */}
      {secondarySponsors.length > 0 && (
        <section className="py-24 section-padding bg-[#0A0A0A] border-y border-white/10" data-testid="secondary-sponsors">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl font-black">PATROCINADORES</h2>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {secondarySponsors.map((sponsor) => (
                <a
                  key={sponsor.id}
                  href={sponsor.website || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-white/10 p-6 hover:border-white/30 transition-all"
                  data-testid={`sponsor-secondary-${sponsor.id}`}
                >
                  <div className="aspect-video flex items-center justify-center mb-4">
                    <img
                      src={sponsor.logo_url}
                      alt={sponsor.name}
                      className="max-h-16 w-auto object-contain grayscale group-hover:grayscale-0 transition-all"
                    />
                  </div>
                  <h3 className="font-heading font-bold text-center text-sm">{sponsor.name}</h3>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Support Sponsors */}
      {supportSponsors.length > 0 && (
        <section className="py-24 section-padding" data-testid="support-sponsors">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl font-black">APOIOS</h2>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-12">
              {supportSponsors.map((sponsor) => (
                <a
                  key={sponsor.id}
                  href={sponsor.website || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sponsor-logo"
                  data-testid={`sponsor-support-${sponsor.id}`}
                >
                  <img
                    src={sponsor.logo_url}
                    alt={sponsor.name}
                    className="h-12 w-auto object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {sponsors.length === 0 && !loading && (
        <section className="py-24 section-padding">
          <div className="container-custom">
            <div className="text-center py-16 border border-white/10">
              <Handshake size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-gray-500">Informação de patrocinadores em breve</p>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 section-padding bg-[#0A0A0A] border-t border-white/10">
        <div className="container-custom">
          <div className="bg-white text-black p-12 md:p-16 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-black mb-4">
              QUERES SER NOSSO PARCEIRO?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Junta-te ao GDR Esporões e apoia o futebol da nossa terra. 
              Contacta-nos para conhecer os nossos planos de patrocínio.
            </p>
            <a
              href="/contactos"
              className="inline-block bg-black text-white px-8 py-4 uppercase font-bold tracking-widest text-sm hover:bg-gray-900 transition-colors"
            >
              CONTACTAR
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SponsorsPage;
